import { useMemo } from 'react'
import { Filters } from '@/service-library/request-types'
import { useGetLiveListConfigs } from '@/services/hooks/relay/dynamic'
import { Document, DocumentRow, DocumentRowValue } from '@/types/documents'
import { StoredFilter } from '@/types/live-list-filter'
import { createFilterBuilder } from '@/utils/live-list-filter-builder'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'

// The builder runs again on every render of every live list field, so one
// broken config would print the same message over and over. This set remembers
// which messages were already printed, so each one prints only once.
const loggedBuilderFailures = new Set<string>()

// A failure is only an error when there is no fallback: with no live list URL,
// the field can never load its list. When the URL is known, the field still
// loads using the basic filters, so a warning is enough.
function logBuilderFailureOnce(error: unknown, hasFallback: boolean) {
  const message = error instanceof Error ? error.message : String(error)
  const key = `${hasFallback}:${message}`
  if (loggedBuilderFailures.has(key)) return
  loggedBuilderFailures.add(key)
  if (hasFallback) {
    // eslint-disable-next-line no-console
    console.warn(
      'Live list filter builder failed. Falling back to basic filters.',
      error,
    )
  } else {
    // eslint-disable-next-line no-console
    console.error(
      'Live list filter builder failed. The live list cannot load.',
      error,
    )
  }
}

export type UseLiveListFilterBuilderOptions = {
  storedFilter: StoredFilter | null
  infoTypeId: string | null | undefined
  document?: Document
  documentRow?: DocumentRow
  /**
   * Optional resolver from DocumentRowValuesContext. When provided, the hook
   * uses it to resolve row field values instead of reading from
   * documentRow.document_row_values (which may not be populated).
   */
  getFieldDocumentRowValue?: (
    fieldId: string,
    rowId: string | null,
  ) => DocumentRowValue | undefined
  /**
   * Extra params merged into the built filter (org_id, query, etc.).
   * Callers should memoize this object to avoid unnecessary recomputation.
   */
  extra: Filters | null
}

export type UseLiveListFilterBuilderResult = {
  liveListUrl: string | null
  domainFilters: Filters | null
}

/**
 * Builds live-list filter params from configuration.
 * Internally retrieves LiveListConfigs and derives projectGridFieldIdByInfoType
 * from the project context, so callers don't need to supply those.
 *
 * Returns `{ liveListUrl: null, domainFilters: null }` when configs aren't
 * loaded yet or when the builder throws (e.g. required data not ready).
 */
export default function useLiveListFilterBuilder({
  storedFilter,
  infoTypeId,
  document,
  documentRow,
  getFieldDocumentRowValue,
  extra,
}: UseLiveListFilterBuilderOptions): UseLiveListFilterBuilderResult {
  const { fieldsMap, project } = useProjectContext()
  const { liveListConfigs } = useGetLiveListConfigs({
    orgCredentialTypeId: project.org_credential_type_id || '',
  })

  const projectGridFieldIdByInfoType = useMemo(
    () =>
      Object.fromEntries(
        Object.values(fieldsMap)
          .filter((f) => f.info_type_id)
          .map((f) => [f.info_type_id as string, f.id]),
      ),
    [fieldsMap],
  )

  const resolveRowValue = useMemo(() => {
    if (!getFieldDocumentRowValue) return undefined
    return (row: DocumentRow, rowInfoTypeId: string) => {
      const fieldId = projectGridFieldIdByInfoType[rowInfoTypeId]
      if (!fieldId) return undefined
      return getFieldDocumentRowValue(fieldId, row.id)
    }
  }, [getFieldDocumentRowValue, projectGridFieldIdByInfoType])

  return useMemo(() => {
    if (!liveListConfigs || !infoTypeId) {
      return { liveListUrl: null, domainFilters: null }
    }
    let builder: ReturnType<typeof createFilterBuilder> | null = null
    try {
      builder = createFilterBuilder({
        storedFilter,
        infoTypeId,
        liveListConfigs,
        document,
        documentRow,
        projectGridFieldIdByInfoType,
        resolveRowValue,
        extra,
      })
      return {
        liveListUrl: builder.liveListUrl,
        domainFilters: builder.build(),
      }
    } catch (error) {
      logBuilderFailureOnce(error, builder !== null)
      if (builder) {
        return {
          liveListUrl: builder.liveListUrl,
          domainFilters: extra,
        }
      }
      return { liveListUrl: null, domainFilters: null }
    }
  }, [
    liveListConfigs,
    storedFilter,
    infoTypeId,
    document,
    documentRow,
    projectGridFieldIdByInfoType,
    resolveRowValue,
    extra,
  ])
}
